import store from '@/store/index'
import * as unitSensors from '@/common/unitSensors'
import * as security from '@/common/security'
import * as dateUtils from '@/common/dateUtils'

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param widgetProps Object
 *
 * @return Object
 */
const _transformVehicleSummary = (widgetProps) => {
  let clientId = widgetProps.clientId
  if (security.isClient() && !security.belongsToMultipleClients()) {
    clientId = security.user().clients[0].id
  }
  return {
    name: widgetProps.widgetName,
    type: widgetProps.selectedWidgetType,
    client_id: clientId,
    vehicle_group_id: widgetProps.vehicleGroupId,
    period: widgetProps.widgetPeriod,
    limit: widgetProps.widgetLimit,
    select_by: widgetProps.selectBy
  }
}

/**
 * Initializes the steps for vehicle/fleet summary based wizards.
 */
const _initVehicleSummarySteps = () => {
  let steps
  if (security.isClient() && !security.belongsToMultipleClients()) {
    // Filter out the 'select-client' wizard step if
    // the user is not allowed to select clients
    steps = ['select-vehicle-group', 'select-period', 'set-limit', 'set-name', 'save']
  } else {
    steps = ['select-client', 'select-vehicle-group', 'select-period', 'set-limit', 'set-name', 'save']
  }
  addWizardSteps(steps)
}

/**
 * Initializes the steps for unit comms status based wizards.
 */
const _initUnitCommsStatusSteps = () => {
  let steps = []
  if (security.isAboveClient() || security.belongsToMultipleClients()) {
    // The other steps will be added dynamically based on what
    // the user selects in the select_by step.
    steps = ['select-comms-status-by']
  } else {
    steps = ['select-last-x-hours', 'set-name', 'save']
  }
  addWizardSteps(steps)
}

/**
 * Initializes the steps for unit comms history based wizards.
 */
const _initUnitCommsHistorySteps = () => {
  let steps = []
  if (security.isAboveClient() || security.belongsToMultipleClients()) {
    // The other steps will be added dynamically based on what
    // the user selects in the select_by step.
    steps = ['select-comms-history-by']
  } else {
    steps = ['select-last-x-hours', 'set-name', 'save']
  }
  addWizardSteps(steps)
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param widgetProps Object
 *
 * @return Object
 */
const _transformUnitCommsStatus = (widgetProps) => {
  let result = {
    name: widgetProps.widgetName,
    type: widgetProps.selectedWidgetType,
    select_by: widgetProps.selectBy,
    partner_ids: [],
    agency_ids: [],
    client_ids: []
  }

  if (security.isClient() && !security.belongsToMultipleClients()) {
    result.client_ids = [security.user().clients[0].id]
    result.select_by = 'clients'
  } else {
    switch (widgetProps.selectBy) {
      case 'clients':
        result.client_ids = widgetProps.clients.map(client => client.id)
        break

      case 'agencies':
        result.agency_ids = widgetProps.agencies.map(agency => agency.id)
        break

      case 'partners':
        result.partner_ids = widgetProps.partners.map(partner => partner.id)
        break
    }
  }

  return result
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param widgetProps Object
 *
 * @return Object
 */
const _transformUnitCommsHistory = (widgetProps) => {
  let result = {
    name: widgetProps.widgetName,
    type: widgetProps.selectedWidgetType,
    select_by: widgetProps.selectBy,
    hours: parseInt(widgetProps.widgetLastXHours),
    partner_id: null,
    agency_id: null,
    client_id: null
  }

  if (security.isClient() && !security.belongsToMultipleClients()) {
    result.client_id = security.user().clients[0].id
    result.select_by = 'client'
  } else {
    switch (widgetProps.selectBy) {
      case 'client':
        result.client_id = widgetProps.clientId
        break

      case 'agency':
        result.agency_id = widgetProps.agencyId
        break

      case 'partner':
        result.partner_id = widgetProps.partnerId
        break
    }
  }

  return result
}

/**
 * Returns a url for the widget to use when requesting its
 * data from the server. The loadedWidget argument is an
 * object of a widget that was previously loaded from the API
 * using the /dashboards request.
 *
 * @param loadedWidget Object
 *
 * @return string
 */
const _vehicleSummaryReqUrl = (loadedWidget) => {
  // Build the url
  let url
  const clientId = loadedWidget.options.client_id === null
    ? security.user().clients[0].id : loadedWidget.options.client_id
  const widgetType = getWidgetType(loadedWidget.type)
  const requestType = widgetType.requestType
  const date = dateUtils.dateShort()
  const period = loadedWidget.options.period.replace('_', '-')
  const limit = loadedWidget.options.limit

  // Note: Although we can use the loadedWidget.options.select_by property
  // to determine whether to select by client or vehicle group this feature was
  // only added recently so there may be saved widgets without the property.
  if (loadedWidget.options.vehicle_group_id === null || typeof loadedWidget.options.vehicle_group_id === 'undefined') {
    url = `history/summary/vehicles/${requestType}/by-client/${clientId}/date/${date}/period/${period}?limit=${limit}`
  } else {
    const groupId = loadedWidget.options.vehicle_group_id
    url = `history/summary/vehicles/${requestType}/by-vehicle-group/${groupId}/client/${clientId}/date/${date}/period/${period}?limit=${limit}`
  }

  return url
}

/**
 * Returns a url for the widget to use when requesting its
 * data from the server. The loadedWidget argument is an
 * object of a widget that was previously loaded from the API
 * using the /dashboards request.
 *
 * @param loadedWidget Object
 *
 * @return string
 */
const _unitCommsStatusReqUrl = (loadedWidget) => {
  let ids
  switch (loadedWidget.options.select_by) {
    case 'clients':
      ids = loadedWidget.options.clients.map(client => client.id).join(',')
      return `units/comms-status/summary/by-clients/${ids}`

    case 'agencies':
      ids = loadedWidget.options.agencies.map(agency => agency.id).join(',')
      return `units/comms-status/summary/by-agencies/${ids}`

    case 'partners':
      ids = loadedWidget.options.partners.map(partner => partner.id).join(',')
      return `units/comms-status/summary/by-partners/${ids}`

    default:
      return `units/comms-status/summary/totals`
  }
}

/**
 * Returns a url for the widget to use when requesting its
 * data from the server. The loadedWidget argument is an
 * object of a widget that was previously loaded from the API
 * using the /dashboards request.
 *
 * @param loadedWidget Object
 *
 * @return string
 */
const _unitCommsHistoryReqUrl = (loadedWidget) => {
  let id
  const fromMoment = dateUtils.nowUtc().subtract(loadedWidget.options.hours, 'hours')
  const fromDate = dateUtils.dateShort(fromMoment)
  const fromTime = dateUtils.time(fromMoment)

  switch (loadedWidget.options.select_by) {
    case 'client':
      id = loadedWidget.options.client_id
      return `history/comms-history/units/by-client/${id}/since/date/${fromDate}/time/${fromTime}`

    case 'agency':
      id = loadedWidget.options.agency_id
      return `history/comms-history/units/by-agency/${id}/since/date/${fromDate}/time/${fromTime}`

    case 'partner':
      id = loadedWidget.options.partner_id
      return `history/comms-history/units/by-partner/${id}/since/date/${fromDate}/time/${fromTime}`

    default:
      return `history/comms-history/units/totals/since/date/${fromDate}/time/${fromTime}`
  }
}

const _widgetTypes = [
  {
    type: 'fleet_top_dist',
    name: 'Fleet: Top Distances Travelled',
    description: 'Shows the vehicles in your fleet that have travelled the furthest distances over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fal', 'road'], transform: 'down-9 right-14' },
    requestType: 'distances',
    label: 'Distance',
    field: 'distance_km',
    format: (value) => `${value.toFixed(1)} km`,
    raw: (value) => value,
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_trip_counts',
    name: 'Fleet: Top Trip Counts',
    description: 'Shows the vehicles in your fleet that have performed the most trips over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fal', 'road'], transform: 'down-9 right-14' },
    requestType: 'trip-count',
    label: 'Trip Count',
    field: 'trip_count',
    format: (value) => value,
    raw: (value) => value,
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_max_speeds',
    name: 'Fleet: Maximum Speeds Reached',
    description: 'Shows the vehicles in your fleet that have reached the highest maximum speeds over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'tachometer-alt-fastest'], transform: 'down-9 right-15' },
    requestType: 'max-speed',
    label: 'Max. Speed',
    field: 'max_speed',
    format: (value) => `${value.toFixed()} km/h`,
    raw: (value) => value,
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_avg_speeds',
    name: 'Fleet: Highest Average Speeds',
    description: 'Shows the vehicles in your fleet that have reached the highest average speeds over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'tachometer-alt-fastest'], transform: 'down-9 right-15' },
    requestType: 'avg-speed',
    label: 'Avg. Speed',
    field: 'avg_speed',
    format: (value) => `${value.toFixed()} km/h`,
    raw: (value) => value,
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_speed_dist',
    name: 'Fleet: Furthest Speeding Distances',
    description: 'Shows the vehicles in your fleet that have been over-speeding for the furthest distances over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'tachometer-alt-fastest'], transform: 'down-9 right-15' },
    requestType: 'speeding-distances',
    label: 'Speeding Dist.',
    field: 'speeding_distance',
    format: (value) => `${value.toFixed(1)} km`,
    raw: (value) => value,
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_speed_dur',
    name: 'Fleet: Longest Speeding Durations',
    description: 'Shows the vehicles in your fleet that have been over-speeding for the longest durations over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'tachometer-alt-fastest'], transform: 'down-9 right-15' },
    requestType: 'speeding-durations',
    label: 'Speeding Dur.',
    field: 'speeding_duration',
    format: (value) => value,
    raw: (value) => unitSensors.HMSToSecs(value),
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_drive_dur',
    name: 'Fleet: Longest Driving Durations',
    description: 'Shows the vehicles in your fleet that have travelled for the longest durations over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'clock'], transform: 'down-9 right-18' },
    requestType: 'driving-durations',
    label: 'Driving Dur.',
    field: 'driving_duration',
    format: (value) => value,
    raw: (value) => unitSensors.HMSToSecs(value),
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_idling_dur',
    name: 'Fleet: Longest Idling Durations',
    description: 'Shows the vehicles in your fleet that have been idling for the longest durations over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'clock'], transform: 'down-9 right-18' },
    requestType: 'idling-durations',
    label: 'Idling Dur.',
    field: 'idling_duration',
    format: (value) => value,
    raw: (value) => unitSensors.HMSToSecs(value),
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'fleet_top_eng_hours',
    name: 'Fleet: Longest Engine Hours',
    description: 'Shows the vehicles in your fleet that have the longest (running) engine hours over the specified period.',
    component: 'ClientFleetComparisonWidget',
    iconA: { icon: ['fal', 'cars'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'clock'], transform: 'down-9 right-18' },
    requestType: 'engine-hours',
    label: 'Engine Hours',
    field: 'engine_hours',
    format: (value) => value,
    raw: (value) => unitSensors.HMSToSecs(value),
    transformCreateUpdate: _transformVehicleSummary,
    buildRequestUrl: _vehicleSummaryReqUrl,
    initSteps: _initVehicleSummarySteps,
    options: { limit: 10, period: 'today' },
    refreshSecs: 3600,
    steps: []
  },
  {
    type: 'unit_comms_status',
    name: 'Unit Comms Status',
    description: 'Shows the total number of active, inactive, offline and units that have never communicated.',
    component: 'UnitCommsStatusWidget',
    iconA: { icon: ['fa', 'hdd'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'clock'], transform: 'down-9 right-18' },
    requestType: 'unit-comms-status',
    label: null,
    field: null,
    format: (value) => value,
    raw: (value) => value,
    transformCreateUpdate: _transformUnitCommsStatus,
    buildRequestUrl: _unitCommsStatusReqUrl,
    initSteps: _initUnitCommsStatusSteps,
    options: { select_by: 'clients' },
    refreshSecs: 60 * 9,
    steps: []
  },
  {
    type: 'unit_comms_history',
    name: 'Unit Comms History',
    description: 'Shows a history of the total number of active, inactive, offline and units that have never communicated.',
    component: 'UnitCommsHistoryWidget',
    iconA: { icon: ['fa', 'hdd'], transform: 'up-7 right-4' },
    iconB: { icon: ['fa', 'clock'], transform: 'down-9 right-18' },
    requestType: 'unit-comms-history',
    label: null,
    field: null,
    format: (value) => value,
    raw: (value) => value,
    transformCreateUpdate: _transformUnitCommsHistory,
    buildRequestUrl: _unitCommsHistoryReqUrl,
    initSteps: _initUnitCommsHistorySteps,
    options: { select_by: 'client' },
    refreshSecs: 60 * 9,
    steps: []
  }
]

const _widgetTypeMap = new Map()
_widgetTypes.forEach((widgetType) => {
  _widgetTypeMap.set(widgetType.type, widgetType)
})

export default {}

export const fetchFirst = () => {
  return store.dispatch(`dashboards/dashboardsModule/fetchFirst`)
}

export const fetchActive = () => {
  return store.dispatch(`dashboards/dashboardsModule/fetchActive`)
}

export const getMode = () => {
  return store.getters['dashboards/dashboardsModule/getMode']
}

export const setMode = (mode) => {
  return store.commit('dashboards/dashboardsModule/setMode', mode)
}

export const getWizardProp = (property) => {
  return store.getters['dashboards/dashboardsModule/getWizardProps'][property]
}

export const setWizardProp = (property, value) => {
  store.commit('dashboards/dashboardsModule/setWizardProp', { property, value })
}

export const resetWizardProps = () => {
  store.commit('dashboards/dashboardsModule/resetWizardProps')
}

export const getWizardPeriodDescription = () => {
  return getPeriodDescription(getWizardProp('widgetPeriod'))
}

export const getWizardSteps = () => {
  return getWizardProp('steps')
}

export const addWizardSteps = (steps) => {
  let newSteps = getWizardSteps()
  newSteps.push(...steps)
  setWizardProp('steps', newSteps)
}

export const getWizardStepNo = () => {
  return getWizardProp('stepNo')
}

export const setWizardStep = (name) => {
  const stepNo = getWizardSteps().findIndex(step => step === name)
  setWizardProp('stepNo', stepNo)
}

export const incWizardStepNo = () => {
  const stepNo = getWizardStepNo() + 1
  setWizardProp('stepNo', stepNo)
  return stepNo
}

export const decWizardStepNo = () => {
  if (getWizardStepNo() > 0) {
    const stepNo = getWizardStepNo() - 1
    setWizardProp('stepNo', stepNo)
    return stepNo
  } else {
    return 0
  }
}

export const getWizardStep = () => {
  const stepNo = getWizardStepNo()
  const steps = getWizardSteps()
  if (stepNo > steps.length - 1) {
    throw new Error(`Widget Wizard StepNo larger than the total number of steps`)
  } else {
    return steps[stepNo]
  }
}

export const getWidgetTypes = () => {
  return _widgetTypes
}

export const getWidgetType = (type) => {
  return _widgetTypeMap.get(type)
}

export const getSelectedWidgetType = () => {
  return getWidgetType(getWizardProp('selectedWidgetType'))
}

export const setSelectedWidgetType = (type) => {
  setWizardProp('selectedWidgetType', type)
  return getWidgetType(type)
}

export const getWizardStepDescription = () => {
  switch (getWizardStep()) {
    case 'select-widget':
      return `Select one of the widgets below to add to dashboard.`

    case 'select-comms-status-by':
      return `Select the filter for the unit communication statuses.`

    case 'select-comms-history-by':
      return `Select which units to show in the unit communications history.`

    case 'select-last-x-hours':
      return `Select how many hours of data you would like to see.`

    case 'select-client':
      return `Select the client you'd like to use for the widget.`

    case 'select-agency':
      return `Select the agency you'd like to use for the widget.`

    case 'select-partner':
      return `Select the partner you'd like to use for the widget.`

    case 'select-multiple-clients':
      return `Find and select one or more clients.`

    case 'select-multiple-agencies':
      return `Find and select one or more agencies.`

    case 'select-multiple-partners':
      return `Find and select one or more partners.`

    case 'select-vehicle-group':
      return `Select the Vehicle Group you would like to use.`

    case 'select-period':
      return `Select the period which you'd like to see.`

    case 'set-limit':
      return `Select how many results you'd like to see.`

    case 'set-name':
      return `Provide a custom name for the widget if you like.`

    case 'save':
      return `Review and add the widget to your dashboard.`

    default:
      throw new Error(`Unknown step provided for wizard: ` + getWizardStep())
  }
}

export const pushToWidgetRequestQueue = (widget) => {
  store.commit('dashboards/dashboardsModule/pushToWidgetRequestQueue', widget)
}

export const runWidgetRequests = () => {
  return store.dispatch('dashboards/dashboardsModule/runWidgetRequests')
}

export const getWizardProgress = () => {
  return Math.round((getWizardStepNo() / getWizardSteps().length) * 100)
}

export const postWidget = () => {
  return store.dispatch(`dashboards/dashboardsModule/postWidget`)
}

export const getPeriodDescription = (period) => {
  switch (period) {
    case 'today':
      return 'Today'

    case 'yesterday':
      return 'Yesterday'

    case 'this_week':
      return 'This Week'

    case 'last_week':
      return 'Last Week'

    case 'this_month':
      return 'This Month'

    case 'last_month':
      return 'Last Month'
  }

  return ''
}

export const getWidgetByIndex = (index) => {
  return store.getters['dashboards/dashboardsModule/getWidgets'][index]
}

export const getWidgetById = (id) => {
  return store.getters['dashboards/dashboardsModule/getWidgets'].find(widget => widget.id === id)
}

export const recoverFromOriginal = () => {
  store.commit('dashboards/dashboardsModule/recoverFromOriginal')
}

export const setActiveDashboard = () => {
  return store.dispatch('dashboards/dashboardsModule/setActiveDashboard')
}

export const fetchDashboardListing = () => {
  return store.dispatch(`dashboards/dashboardsModule/fetchDashboardListing`)
}

export const getDashboardListing = () => {
  return store.getters[`dashboards/dashboardsModule/getDashboardListing`]
}

export const setActiveDashboardById = (id) => {
  return store.dispatch('dashboards/dashboardsModule/setActiveDashboardById', id)
}

export const removeWidget = (widgetId) => {
  return store.dispatch('dashboards/dashboardsModule/removeWidget', widgetId)
}

export const startRefreshTimer = () => {
  return store.commit('dashboards/dashboardsModule/startRefreshTimer')
}

export const stopRefreshTimer = () => {
  return store.commit('dashboards/dashboardsModule/stopRefreshTimer')
}



// WEBPACK FOOTER //
// ./src/store/dashboards/dashboards.helpers.js